(() => {
  const roots = document.querySelectorAll('[data-ryu-product-compare]');

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const parseProduct = (card) => {
    const node = card?.querySelector('[data-ryu-compare-data]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('RyuGear comparison data could not be read.', error);
      return null;
    }
  };

  roots.forEach((root) => {
    if (root.dataset.compareReady === 'true') return;
    root.dataset.compareReady = 'true';

    const limit = Number(root.dataset.compareLimit || 5);
    const selected = new Map();
    const tray = root.querySelector('[data-ryu-compare-tray]');
    const slots = root.querySelector('[data-ryu-compare-slots]');
    const count = root.querySelector('[data-ryu-compare-count]');
    const openButton = root.querySelector('[data-ryu-compare-open]');
    const status = root.querySelector('[data-ryu-compare-status]');
    const modal = root.querySelector('[data-ryu-compare-modal]');
    const table = root.querySelector('[data-ryu-compare-table]');
    let enabled = false;

    const setStatus = (message) => {
      if (!status) return;
      status.textContent = message;
      window.clearTimeout(setStatus.timer);
      setStatus.timer = window.setTimeout(() => {
        status.textContent = '';
      }, 2600);
    };

    const syncCards = () => {
      root.querySelectorAll('[data-ryu-compare-toggle]').forEach((button) => {
        button.setAttribute('aria-checked', String(enabled));
        button.setAttribute('aria-label', enabled ? 'Disable product comparison' : 'Enable product comparison');
      });

      root.querySelectorAll('[data-ryu-compare-product]').forEach((card) => {
        const id = String(card.dataset.productId);
        const button = card.querySelector('[data-ryu-compare-select]');
        const isSelected = selected.has(id);
        card.classList.toggle('is-compare-selected', isSelected);
        button?.setAttribute('aria-pressed', String(isSelected));
        if (button) {
          const product = selected.get(id) || parseProduct(card);
          if (product) {
            button.setAttribute(
              'aria-label',
              isSelected ? `Remove ${product.title} from comparison` : `Add ${product.title} to comparison`
            );
          }
        }
      });
    };

    const renderTray = () => {
      if (!tray || !slots || !count || !openButton) return;
      tray.hidden = selected.size === 0;
      count.textContent = `Compare (${selected.size}/${limit})`;
      openButton.disabled = selected.size < 2;

      const products = [...selected.values()];
      slots.innerHTML = Array.from({ length: limit }, (_, index) => {
        const product = products[index];
        if (!product) {
          return '<span class="ryu-compare-tray__slot ryu-compare-tray__slot--empty" aria-hidden="true"></span>';
        }

        return `
          <span class="ryu-compare-tray__slot" title="${escapeHtml(product.title)}">
            ${product.image ? `<img src="${escapeHtml(product.image)}" alt="" width="72" height="72" loading="eager">` : ''}
            <button type="button" aria-label="Remove ${escapeHtml(product.title)}" data-ryu-compare-remove="${escapeHtml(product.id)}">×</button>
          </span>`;
      }).join('');
      syncCards();
    };

    const cardMarkup = (product) => `
      <article class="ryu-compare-table__product">
        <button type="button" class="ryu-compare-table__remove" aria-label="Remove ${escapeHtml(product.title)}" data-ryu-compare-remove="${escapeHtml(product.id)}">×</button>
        <a href="${escapeHtml(product.url)}" class="ryu-compare-table__image">
          ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="360" height="360" loading="eager">` : ''}
        </a>
        <h3>${escapeHtml(product.title)}</h3>
        <a class="ryu-compare-table__details" href="${escapeHtml(product.url)}">View details</a>
      </article>`;

    const comparisonFields = [
      ['Price', 'price'],
      ['Availability', 'availability'],
      ['Brand', 'vendor'],
      ['Product type', 'type'],
      ['Variants', 'variants'],
      ['Options', 'options'],
      ['Description', 'description'],
    ];

    const mobileCardMarkup = (product, index, total) => `
      <article class="ryu-compare-mobile__card">
        <span class="ryu-compare-mobile__count">Product ${index + 1} of ${total}</span>
        <button type="button" class="ryu-compare-table__remove" aria-label="Remove ${escapeHtml(product.title)}" data-ryu-compare-remove="${escapeHtml(product.id)}">×</button>
        <a href="${escapeHtml(product.url)}" class="ryu-compare-mobile__image">
          ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="480" height="480" loading="eager">` : ''}
        </a>
        <h3>${escapeHtml(product.title)}</h3>
        <a class="ryu-compare-table__details" href="${escapeHtml(product.url)}">View details</a>
        <dl class="ryu-compare-mobile__details">
          ${comparisonFields.map(([label, key]) => `
            <div>
              <dt>${escapeHtml(label)}</dt>
              <dd>${escapeHtml(product[key] || '—')}</dd>
            </div>`).join('')}
        </dl>
      </article>`;

    const rowMarkup = (label, products, key, modifier = '') => `
      <div class="ryu-compare-table__row ${modifier}">
        <strong class="ryu-compare-table__label">${escapeHtml(label)}</strong>
        ${products.map((product) => `<div class="ryu-compare-table__value">${escapeHtml(product[key] || '—')}</div>`).join('')}
      </div>`;

    const renderModal = () => {
      if (!table) return;
      const products = [...selected.values()];
      const columns = products.length;
      table.style.setProperty('--compare-columns', columns);
      table.innerHTML = `
        <div class="ryu-compare-mobile">
          <p class="ryu-compare-mobile__hint">Swipe to compare ${products.length} products</p>
          <div class="ryu-compare-mobile__cards">
            ${products.map((product, index) => mobileCardMarkup(product, index, products.length)).join('')}
          </div>
        </div>
        <div class="ryu-compare-desktop">
          <div class="ryu-compare-table__products">
            <span class="ryu-compare-table__corner" aria-hidden="true"></span>
            ${products.map(cardMarkup).join('')}
          </div>
          ${rowMarkup('Price', products, 'price', 'ryu-compare-table__row--price')}
          ${rowMarkup('Availability', products, 'availability')}
          ${rowMarkup('Brand', products, 'vendor')}
          ${rowMarkup('Product type', products, 'type')}
          ${rowMarkup('Variants', products, 'variants')}
          ${rowMarkup('Options', products, 'options')}
          ${rowMarkup('Description', products, 'description', 'ryu-compare-table__row--description')}
        </div>
      `;
    };

    const closeModal = () => {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      document.body.classList.remove('ryu-compare-modal-open');
    };

    const openModal = () => {
      if (!modal || selected.size < 2) return;
      renderModal();
      modal.hidden = false;
      document.body.classList.add('ryu-compare-modal-open');
      table.scrollTop = 0;
      table.scrollLeft = 0;
      const mobileCards = table.querySelector('.ryu-compare-mobile__cards');
      if (mobileCards) mobileCards.scrollLeft = 0;
      modal.querySelector('[data-ryu-compare-close]:not(.ryu-compare-modal__backdrop)')?.focus();
    };

    const removeProduct = (id) => {
      selected.delete(String(id));
      renderTray();
      if (!modal?.hidden) {
        if (selected.size < 2) closeModal();
        else renderModal();
      }
    };

    root.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-ryu-compare-toggle]');
      if (toggle) {
        enabled = !enabled;
        root.classList.toggle('is-compare-mode', enabled);
        toggle.setAttribute('aria-checked', String(enabled));
        toggle.setAttribute('aria-label', enabled ? 'Disable product comparison' : 'Enable product comparison');
        return;
      }

      const selector = event.target.closest('[data-ryu-compare-select]');
      if (selector) {
        event.preventDefault();
        event.stopPropagation();
        const card = selector.closest('[data-ryu-compare-product]');
        const id = String(card?.dataset.productId || '');

        if (selected.has(id)) {
          removeProduct(id);
          return;
        }

        if (selected.size >= limit) {
          setStatus(`You can compare up to ${limit} products.`);
          return;
        }

        const product = parseProduct(card);
        if (!product) return;
        selected.set(id, product);
        renderTray();
        setStatus(`${product.title} added to comparison.`);
        return;
      }

      const remove = event.target.closest('[data-ryu-compare-remove]');
      if (remove) {
        removeProduct(remove.dataset.ryuCompareRemove);
        return;
      }

      if (event.target.closest('[data-ryu-compare-clear]')) {
        selected.clear();
        renderTray();
        closeModal();
        return;
      }

      if (event.target.closest('[data-ryu-compare-open]')) {
        openModal();
        return;
      }

      if (event.target.closest('[data-ryu-compare-close]')) closeModal();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal();
    });

    const observer = new MutationObserver(syncCards);
    observer.observe(root, { childList: true, subtree: true });

    renderTray();
  });
})();
